/**
 * Which backends may run mock mode, shared by the client (`lib/mockMode.ts`)
 * and the build guard below (imported by `next.config.mjs`).
 *
 * An ALLOW-list, not a prd deny-list: a new or misspelt environment name
 * (`prod`, `preprod`, `staging`, …) is refused rather than silently let in.
 * The values `next.config.mjs` produces are `uat` (its default) and `prd`
 * (`<env>.<platform>.endor-group.net` hosts: `uat.development`,
 * `prd.production`); `dev` is allowed for a future dev backend.
 */
export const MOCK_MODE_ALLOWED_BACKEND_ENVS = Object.freeze(["uat", "dev"]);

// `next.config.mjs` defaults (`BACKEND_ENVIRONMENT || "uat"`,
// `BACKEND_PLATFORM || "development"`). next.config always inlines both as
// NEXT_PUBLIC_BACKEND_*, so "unset" only happens outside a Next build
// (unit tests), where it means these same defaults.
const DEFAULT_BACKEND_ENV = "uat";
const DEFAULT_BACKEND_PLATFORM = "development";

/**
 * @param {{ backendEnv: string | undefined; backendPlatform: string | undefined }} env
 * @returns {boolean}
 */
export function isMockModeAllowedForBackend({ backendEnv, backendPlatform }) {
  const env = backendEnv ?? DEFAULT_BACKEND_ENV;
  const platform = backendPlatform ?? DEFAULT_BACKEND_PLATFORM;
  return (
    MOCK_MODE_ALLOWED_BACKEND_ENVS.includes(env) && platform !== "production"
  );
}

/**
 * Build-time guard for runtime mock mode, imported by `next.config.mjs`.
 * A build against a backend outside the allow-list must never ship with
 * `NEXT_PUBLIC_MOCK_MODE=true`: the client ignores it there anyway
 * (`lib/mockMode.ts`), so the build fails loudly instead of silently producing
 * a bundle whose config says otherwise.
 *
 * @param {{ mockMode: string | undefined; backendEnv: string | undefined; backendPlatform: string | undefined }} env
 * @returns {void}
 */
export function assertMockModeBuildEnv({
  mockMode,
  backendEnv,
  backendPlatform,
}) {
  if (
    mockMode === "true" &&
    !isMockModeAllowedForBackend({ backendEnv, backendPlatform })
  ) {
    throw new Error(
      `NEXT_PUBLIC_MOCK_MODE=true cannot be built against BACKEND_ENVIRONMENT=${backendEnv} / BACKEND_PLATFORM=${backendPlatform}. ` +
        `Mock mode serves a fake user from MSW and is only allowed on ${MOCK_MODE_ALLOWED_BACKEND_ENVS.join("/")} backends off the production platform — ` +
        "unset NEXT_PUBLIC_MOCK_MODE or build against an allowed backend."
    );
  }
}
